import { useEffect, useState, useRef } from "react";
import {
  MaterPageTitle,
  MatterPageContainer,
  MatterPageContentWrapper,
  MatterPageWrapper,
  MyMattersToggle,
  PaginationContainer,
} from "./MatterStyled";
import Table from "../../components/Table/Table";
import MatterListSkeleton from "../../components/Table/MatterListSkeleton";
import { MatterTableColumn } from "../../components/Table/columns/MatterTableColumn";
import Search from "../../components/Search/Search";
import PillDropdown from "../../components/PillDropdown/PillDropdown";
import Pagination from "../../components/Pagination/Pagination";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  setAssignedToMe,
  setCurrentPage,
  setPageSize,
  setSelectedValue,
  setSelectedValueCaseType,
} from "../../slice/Matterslice";
import ErrorToast from "../../components/Notification/ErrorToast";
import { CASE_TYPE_DROPDOWN_OPTIONS, fetchMatterData, handleAssignedToMeToggle, handleCaseTypeSelect, handleSelect, handlePageChange, handlePageSizeChange } from "./MatterUtils";

const Matter = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showNotification, setShowNotification] = useState(false);
  const [error, setError] = useState([]);
  const {
    matterList,
    totalRecords,
    sort,
    item,
    currentPage,
    pageSize,
    selectedValue,
    selectedValueCaseType,
    assignedToMe,
    search,
  } = useSelector((state) => state.matter);

  const [isLoading, setIsLoading] = useState(false);

  // Call MatterTableColumn unconditionally to avoid hook order issues
  const tableColumns = MatterTableColumn();

  function handleError(message) {
    setIsLoading(false);
    const id = Date.now().toString();
    setError([{ id, newMsg: message }]);
    setShowNotification(true);
  }

  useEffect(() => {
    // Overwrite the current entry so Salesforce isn’t in stack
    globalThis.history.replaceState(null, "", "/matter");
    const handlePopState = () => {
      console.log("Blocked back navigation to SSO");
      navigate("/matter", { replace: true });
    };
    globalThis.addEventListener("popstate", handlePopState);
    return () => {
      globalThis.removeEventListener("popstate", handlePopState);
    };
  }, [navigate]);

  useEffect(() => {
    const initializeData = async () => {
      try {
        const payload = {
          dispatch,
          selectedValueCaseType,
          selectedValue,
          currentPage,
          sort,
          item,
          pageSize,
          search,
          assignedToMe,
          setIsLoading,
          handleError
        };
        // Fetch the matter data
        await fetchMatterData(payload);
      } catch (error) {
        navigate("/");
        console.error("Initialization Error:", error);
        handleError("Failed to initialize data");
      } finally {
        setIsLoading(false);
      }
    };

    initializeData();
    // eslint-disable-next-line
  }, [
    dispatch,
    currentPage,
    selectedValue,
    selectedValueCaseType,
    assignedToMe,
    sort,
    item,
    search,
    pageSize,
  ]);


  // eslint-disable-next-line

  const getRowProps = () => (record) => ({
    onClick: (e) => {
      e.stopPropagation();
      if(record.last_opened_relative_path){
      navigate(`/matter${record.last_opened_relative_path}`, { state: { record: record } });
      }else{
      navigate(`/matter/${record.id}`, { state: { record: record } });
      }
     
    },
  });

  return (
    <MatterPageWrapper>
      <MatterPageContainer>
        <MaterPageTitle>Matters</MaterPageTitle>
        <MatterPageContentWrapper>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <MyMattersToggle
              type="button"
              $active={Boolean(assignedToMe)}
              aria-pressed={Boolean(assignedToMe)}
              data-testid="my-matters-toggle"
              onClick={() =>
                handleAssignedToMeToggle(
                  dispatch,
                  !assignedToMe,
                  setAssignedToMe,
                  setCurrentPage
                )
              }
            >
              My Matters
            </MyMattersToggle>
            <PillDropdown
              handleChange={(value) => handleCaseTypeSelect(dispatch, value, setSelectedValueCaseType, setCurrentPage)}
              value={selectedValueCaseType}
              testId="case-type-dropdown"
              options={CASE_TYPE_DROPDOWN_OPTIONS}
              placeholder="Case Type"
            />
            <PillDropdown
              handleChange={(value) => handleSelect(dispatch, value, setSelectedValue, setCurrentPage)}
              value={selectedValue}
              testId={"status-dropdown"}
              options={[
                {
                  value: "Open",
                  label: "Open",
                },
                {
                  value: "Closed",
                  label: "Closed",
                },
                {
                  value: "All",
                  label: "All",
                },
              ]}
              placeholder="Status"
            />
          </div>
          <div style={{ flexShrink: 0, minWidth: 450 }}>
            <Search
              searchPlaceholder={
                "Search Matter/ Client/ Case Manager/ Principal Attorney"
              }
              isLoading={isLoading}
              searchbarwidth="450px"
              searchbarheight="32px"
            />
          </div>
        </MatterPageContentWrapper>
      </MatterPageContainer>
      <div style={{ position: 'relative', minHeight: '400px' }}>
        <Table
          columns={tableColumns}
          data={matterList}
          loading={isLoading}
          loadingComponent={<MatterListSkeleton rows={pageSize || 10} />}
          onRow={getRowProps()}
          emptyMessage={"No records found"}
        />
        {!isLoading && matterList.length > 0 && (
          <PaginationContainer>
            <Pagination
              total={totalRecords}
              current={currentPage}
              pageSize={pageSize}
              onChange={(page) =>
                handlePageChange(dispatch, page, setIsLoading, setCurrentPage)
              }
              adjustable={false}
              onPageSizeChange={(pageSize) =>
                handlePageSizeChange(
                  dispatch,
                  pageSize,
                  setCurrentPage,
                  setPageSize
                )
              }
            />
          </PaginationContainer>
        )}
      </div>
      {showNotification && (
        <ErrorToast
          message={error}
          onClose={(id) =>
            //setShowNotification(false)
            setError((prev) => prev.filter((msg) => msg.id !== id))
          } // Hide notification when closed
        />
      )}
    </MatterPageWrapper>
  );
};

export default Matter;